// public/v3/src/lib/fax-sectionize.js — reading-order + sectioning for the form-fill screen.
//
// Extracted from fax-form-fill-screen.jsx so the ordering logic is unit-testable (Jest has no JSX
// transform in this repo). CommonJS so BOTH consumers work: esbuild bundles the frontend via its
// CommonJS→ESM interop (`import { sectionize } from '../lib/fax-sectionize'`), and Jest requires it.
//
// displayOrder: a conditional write-in ("Please specify" / "Other" / "If yes …") is a SEPARATE text
// field that belongs right under the radio option it follows. But alignment can corrupt that write-in's
// answerBox.y, so a pure y-sort floats it away from its radio (observed: Optum reason-for-exceeding
// "Please specify" rendered above the radio group). We keep the y-sort for general fields and RE-ATTACH
// each conditional write-in immediately after its parent choice field, using the DETECTION order
// (field.order / numeric id) — set before alignment moves boxes and carried through to the client.

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

// Detection reading-order key: field.order (set in locateFormFields _finalize) or the numeric part of a
// detector id 'f<N>'. null for backfilled fields (ids like 'bf1_0' / 'bt1_0', no order) → geometry only.
function orderKey(f) {
  if (f && Number.isFinite(Number(f.order))) return Number(f.order);
  const m = /^f(\d+)$/.exec(String((f && f.id) || ''));
  return m ? Number(m[1]) : null;
}

function ySort(a, b) {
  const pa = Number(a.page) || 1, pb = Number(b.page) || 1;
  if (pa !== pb) return pa - pb;
  const ya = (a.answerBox && a.answerBox.y) || 0, yb = (b.answerBox && b.answerBox.y) || 0;
  if (Math.abs(ya - yb) > 0.001) return ya - yb;
  return ((a.answerBox && a.answerBox.x) || 0) - ((b.answerBox && b.answerBox.x) || 0);
}

const isChoiceLike = (f) => !!f && ['radio', 'choice', 'boolean_ynd'].includes(f.fieldType);

// The renderable options for a choice-like field. boolean_ynd synthesizes Yes/No/Declined (matching the
// radio control), so option-code validation, nesting, and rendering treat it identically to an
// options-bearing radio — a shared source of truth prevents a field being both excluded AND double-rendered.
function fieldOptions(f) {
  if (!f) return [];
  if (f.fieldType === 'boolean_ynd') return [{ code: 'Y', label: 'Yes' }, { code: 'N', label: 'No' }, { code: 'D', label: 'Declined / unknown' }];
  return Array.isArray(f.options) ? f.options.filter(Boolean) : [];
}

// A conditional write-in: a TEXT field whose label is a follow-on prompt that trails a radio option on
// the source form ("Please specify", "Other", "If yes …"). Kept tight so a real standalone free-text
// field like "any other comments … important to this review" is NOT swept up (it must stay in place).
function isConditionalWriteIn(f) {
  if (!f || f.fieldType !== 'text') return false;
  const t = norm(f.label);
  return t === 'please specify' || t.startsWith('please specify ')
    || t === 'other' || t === 'other please specify'
    || t.startsWith('if yes') || t.startsWith('if no');
}

const sameScope = (a, b) =>
  (Number(a.page) || 1) === (Number(b.page) || 1) && (a.section || '') === (b.section || '');

// A write-in bound by the server association pass to a SPECIFIC option (`conditionalFor.scope==='option'`)
// whose parent radio is actually present renders INSIDE that radio, so it's pulled out of the top-level
// flow. A dangling link (parent absent) returns null → the write-in stays top-level (safe fallback).
function optionBoundParent(f, byId) {
  const c = f && f.conditionalFor;
  if (!c || c.scope !== 'option' || c.parentFieldId == null) return null;
  const p = byId.get(c.parentFieldId);
  if (!p || !isChoiceLike(p)) return null;
  // The bound option code must actually exist on the parent — otherwise the radio control can't nest it,
  // and excluding it from the top-level would ORPHAN the field. If it doesn't match, keep it top-level.
  const code = String(c.parentOptionCode);
  return fieldOptions(p).some((o) => String(o.code) === code) ? p : null;
}

// Map of optionCode -> [write-in fields] bound to the given radio, for the radio control to nest them
// directly under the matching option.
function optionBoundWriteIns(fields, radioId) {
  const out = {};
  for (const f of (fields || [])) {
    const c = f && f.conditionalFor;
    if (c && c.scope === 'option' && c.parentFieldId === radioId && c.parentOptionCode != null) {
      const k = String(c.parentOptionCode);
      (out[k] = out[k] || []).push(f);
    }
  }
  return out;
}

// Reading order for display. General fields keep the (aligned) y-order; each conditional write-in is
// pulled out and re-attached immediately after its parent choice field — the nearest non-conditional
// predecessor in DETECTION order within the same page/section. A write-in with no choice-like parent
// (or whose parent is across a boundary) stays in y-order. No field is dropped or duplicated.
function displayOrder(fields) {
  const list = (fields || []).slice();
  const byId = new Map(list.map((f) => [f.id, f]));
  // Option-bound write-ins (present, choice-like parent) render INSIDE the radio control → drop them from
  // the top-level flow so they don't also appear as standalone rows.
  const nested = new Set(list.filter((f) => optionBoundParent(f, byId)).map((f) => f.id));
  const top = list.filter((f) => !nested.has(f.id));
  const yOrdered = top.slice().sort(ySort);
  const detection = top.slice().sort((a, b) => {
    const oa = orderKey(a), ob = orderKey(b);
    if (oa != null && ob != null) return oa - ob;
    if (oa != null) return -1;        // detector fields (with order) precede backfills (without)
    if (ob != null) return 1;
    return ySort(a, b);
  });
  const attach = new Map();           // parent field -> [write-ins], in detection order
  const attached = new Set();         // write-ins that were re-homed
  detection.forEach((f, i) => {
    // Only re-home a conditional write-in that is a DETECTOR field (has a reliable `order`). A backfilled
    // field (id 'bf1_0'/'bt1_0', no order) has no trustworthy reading position, so leave it in y-order
    // rather than risk attaching it to the wrong parent. Conditional write-ins are detector-origin in
    // practice (backfill only recovers identity cells), so this just guards a contrived input.
    if (!isConditionalWriteIn(f) || orderKey(f) == null) return;
    for (let j = i - 1; j >= 0; j--) {
      const p = detection[j];
      if (!sameScope(f, p)) break;              // never cross a page/section boundary
      if (isConditionalWriteIn(p)) continue;    // skip sibling write-ins to reach the real parent
      if (isChoiceLike(p) && orderKey(p) != null) { if (!attach.has(p)) attach.set(p, []); attach.get(p).push(f); attached.add(f); } // parent must be an ordered detector field too
      break;                                     // the first non-conditional predecessor decides
    }
  });
  const emitted = new Set();   // emit each field at most once, even if the input array repeats a reference
  return yOrdered.flatMap((f) => {
    if (attached.has(f) || emitted.has(f)) return [];
    emitted.add(f);
    return [f, ...(attach.get(f) || [])];
  });
}

// writeInInactive(field, byId, valueOf) → true when an option-bound write-in's parent question
// holds a different option (valueOf(fieldId) returns the parent's effective value). This is THE
// suppression predicate for BOTH sides — the screen (disable the nested input, skip its flush, skip
// its pending-position row) and the server (formFillSession.conditionalSuppressed → render/complete
// gates) require this one function, so what the doctor sees can never diverge from what prints. The
// sides differ only in valueOf: the screen passes the value the parent will hold after the Review
// flush; the server passes confirmedValueOf(answers). Fail-open throughout: no/dangling/self link,
// a non-choice parent, an option code the parent doesn't actually have (all via optionBoundParent),
// or an unanswered parent → false (the field behaves like any other; a wrong association can dim a
// value, never destroy one).
function writeInInactive(f, byId, valueOf) {
  const p = optionBoundParent(f, byId);
  if (!p) return false;
  const v = valueOf(p.id);
  if (v == null || String(v) === '') return false;
  return String(v) !== String(f.conditionalFor.parentOptionCode);
}

// valueOf over a saved answers map with PRINT-TIME semantics: only a CONFIRMED, non-empty answer
// counts (an unconfirmed suggestion renders nothing, so it must never suppress). Used by the server
// gates and by the screen's review-phase position list, which mirrors them.
const confirmedValueOf = (answers) => (id) => {
  const a = (answers || {})[id];
  return a && a.confirmed === true && a.value != null && String(a.value) !== '' ? a.value : null;
};

// ── Entry-grid values (fields with `gridRows`: repeated "____ Date(s): ___" rows) ─────────────────
// Wire format: {"entries":[{"text","date"}...]}. A plain string still works end-to-end (the render
// falls back to the paragraph wrap), and a seeded chart suggestion arrives plain — gridParse splits
// it into rows for editing. gridSerialize is POSITIONAL (only trailing empties drop) so rows never
// compact under the user mid-edit; the render skips interior empties itself.
function gridParse(value, maxRows) {
  const s = String(value == null ? '' : value);
  const cap = Math.max(1, Number(maxRows) || 1);
  try {
    const v = JSON.parse(s);
    if (v && Array.isArray(v.entries)) {
      const entries = v.entries.map((e) => ({ text: String((e && e.text) || ''), date: String((e && e.date) || '') }));
      return entries.length ? entries.slice(0, cap) : [{ text: '', date: '' }];
    }
  } catch (_) { /* plain string */ }
  if (!s.trim()) return [{ text: '', date: '' }];
  const parts = s.split(/,\s+(?=[A-Z0-9])/).map((t) => t.trim()).filter(Boolean);
  if (parts.length <= 1) return [{ text: s.trim(), date: '' }];
  return parts.slice(0, cap).map((t, i) => ({ text: (i === cap - 1 && parts.length > cap) ? parts.slice(i).join(', ') : t, date: '' }));
}
function gridSerialize(entries) {
  const norm = (entries || []).map((e) => ({ text: String((e && e.text) || ''), date: String((e && e.date) || '') }));
  let end = norm.length;
  while (end > 0 && !norm[end - 1].text.trim() && !norm[end - 1].date.trim()) end--;
  const kept = norm.slice(0, end);
  return kept.length ? JSON.stringify({ entries: kept }) : '';
}

// Group fields into sections, form-agnostically. Prefer a detector-provided section label; else group by
// page. Returns [{ title|null, fields[] }] in reading order (displayOrder).
function sectionize(fields) {
  const ordered = displayOrder(fields);
  const groups = [];
  let cur = null;
  const pages = new Set(ordered.map((f) => Number(f.page) || 1));
  const multi = pages.size > 1;
  for (const f of ordered) {
    const key = f.section || (multi ? `Page ${Number(f.page) || 1}` : '');
    if (!cur || cur.key !== key) { cur = { key, title: f.section || (multi ? `Page ${Number(f.page) || 1}` : null), fields: [] }; groups.push(cur); }
    cur.fields.push(f);
  }
  return groups;
}

module.exports = { sectionize, optionBoundWriteIns, fieldOptions, writeInInactive, confirmedValueOf, gridParse, gridSerialize, __test: { displayOrder, isConditionalWriteIn, orderKey, optionBoundWriteIns, fieldOptions, optionBoundParent, writeInInactive, confirmedValueOf, gridParse, gridSerialize } };
